import { getClubInformation } from '../content/club-look-ups-by-id';
import type { BuildType } from '../enums/build/build-type';
import {
  getAllLanguagesForBuildType,
  getOrderedLanguagesForBuildType,
} from '../enums/build/build-type';
import { Language } from '../enums/language';

export function isDefaultLanguageForClub(clubId: string, language: Language): boolean {
  return getClubDefaultLanguage(clubId) === language;
}

export function getClubDefaultLanguage(clubId: string): Language {
  const buildType = getClubInformation(clubId).buildType;
  return getOrderedLanguagesForBuildType(buildType).default;
}

function getLanguageSubtag(language: string): string {
  // Assumes language follows conventions described by BCP 47 (https://en.wikipedia.org/wiki/IETF_language_tag)
  return language.split('-')[0].toUpperCase();
}

export const languageFromCurrentUrlOrBuildTypeDefault = (buildType: BuildType): Language => {
  // This is known to cause React hydration errors, so use with caution!
  if (typeof window === 'undefined') {
    return getOrderedLanguagesForBuildType(buildType).default;
  }
  const firstSegment =
    window.location.pathname.split('/').find((segment) => segment.length > 0) ?? '';
  return getBestLanguageMatch(firstSegment, buildType);
};

export function getBestLanguageMatch(
  language: string,
  buildType: BuildType,
  languagesOverride?: Language[]
): Language {
  const languagesForBuildType = getAllLanguagesForBuildType(buildType);
  const countryIndependentLanguage = getLanguageSubtag(language);
  const defaultLanguage = getOrderedLanguagesForBuildType(buildType).default;

  if (!!languagesOverride?.length) {
    // If the language override language is the language requested, use that
    const exactOverrideMatch = languagesOverride.find(
      (overrideLanguage) => overrideLanguage === (language as Language)
    );
    if (exactOverrideMatch) {
      return exactOverrideMatch;
    }

    // If the language override is a variant of the language requested, use that
    const variantOverrideMatch = languagesOverride.find(
      (overrideLanguage) => countryIndependentLanguage === getLanguageSubtag(overrideLanguage)
    );
    if (variantOverrideMatch) {
      return variantOverrideMatch;
    }

    // If the language override is the default for the build type, use that
    if (languagesOverride.some((overrideLanguage) => overrideLanguage === defaultLanguage)) {
      return defaultLanguage;
    }

    return languagesOverride[0];
  }

  // If the build type supports the exact language requested, use that
  const exactBuildMatch = languagesForBuildType.find(
    (buildLanguage) => buildLanguage === (language as Language)
  );
  if (exactBuildMatch) {
    return exactBuildMatch;
  }

  // If the build type supports a variant of the language requested, use that
  const variantBuildMatch = languagesForBuildType.find(
    (buildLanguage) => countryIndependentLanguage === getLanguageSubtag(buildLanguage)
  );
  if (variantBuildMatch) {
    return variantBuildMatch;
  }

  // Use the default language for the build type
  return getOrderedLanguagesForBuildType(buildType).default;
}

/**
 * Returns the best matching {@link Language} from all available languages.
 * @param language - The language string to find the best match for.
 * @param buildTypeForDefaultLanguage - Only used to determine the default language to fall back to.
 *   This function matches against all languages, so it can return languages not supported by the given build type.
 */
export function getBestLanguageMatchFromAllLanguages(
  language: string,
  buildTypeForDefaultLanguage: BuildType
): Language {
  return getBestLanguageMatch(language, buildTypeForDefaultLanguage, Object.values(Language));
}
